import { Alert, Button, cardClass, textareaClass } from '@ledgerloom/ui';
import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useApi, usePoll } from '../hooks';
import { ErrorBanner, Icon, StateChip, Time } from './ui';

interface Task { id: string; agent_id: string | null; kind: string; subject: { message?: string } | null; status: string; proposal_id: string | null; created_at: string }
interface Proposal { id: string; state: string }

// A designer's requests (forms/textarea, ai-intelligence/ai-loading-states): a
// request becomes workClass work for the hired template; what it proposes waits
// under Needs you for an admin's signature. The workspace designer beside the
// business model, the workflow designer beside the workflows.
export function DesignerRequests({ template, workClass, title, intro, className, onChanged }: {
  template: string; workClass: string; title: string; intro: string; className?: string; onChanged?: () => void;
}) {
  const headingId = useId();
  const fieldId = useId();
  const agents = useApi<Array<{ id: string; template_id: string | null; status: string }>>('/v1/agents');
  const designer = agents.data?.find((a) => a.template_id === template);
  const tasks = useApi<Task[]>(designer ? `/v1/agent-tasks?agent=${encodeURIComponent(designer.id)}` : null, [designer?.id]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mine = (tasks.data ?? []).filter((t) => t.kind === workClass).slice(-5).reverse();
  const waiting = mine.some((t) => ['queued', 'claimed'].includes(t.status));
  // While the designer works, its requests and the page behind them refresh.
  usePoll(waiting, () => { void tasks.reload(); onChanged?.(); });

  const ask = async (e: FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true); setError(null);
    try {
      await api('/v1/work', { body: { class: workClass, subject: { message: message.trim() }, title: message.trim().slice(0, 80) } });
      setMessage('');
      void tasks.reload();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <aside className={`${cardClass} card-pad stack ${className ?? ''}`} aria-labelledby={headingId} style={{ gap: 12 }}>
      <h2 id={headingId}>{title}</h2>
      <p className="faint">{intro}</p>
      {designer && designer.status !== 'active' && <Alert role="note" tone="info">The designer is {designer.status}; its proposals reach Needs you once it is active.</Alert>}
      <form onSubmit={ask} className="stack" style={{ gap: 8 }}>
        <div className="field">
          <label htmlFor={fieldId}>Describe the change</label>
          <textarea id={fieldId} className={textareaClass} rows={3} value={message} maxLength={2000} onChange={(e) => setMessage(e.target.value)} />
        </div>
        <ErrorBanner error={error} />
        <div><Button type="submit" disabled={busy || !message.trim() || !designer}><Icon name="agent" />Ask the designer</Button></div>
      </form>
      {waiting && <div role="status" className="faint">The designer is working on it…</div>}
      <ol className="timeline" aria-label="Your requests, newest first">{mine.map((t) => <Request key={t.id} t={t} />)}</ol>
    </aside>
  );
}

function Request({ t }: { t: Task }) {
  const p = useApi<Proposal>(t.proposal_id ? `/v1/proposals/${t.proposal_id}` : null, [t.proposal_id, t.status]);
  return (
    <li>
      <div>{t.subject?.message ?? 'A request'}</div>
      <div className="faint row" style={{ gap: 6 }}>
        {t.proposal_id && p.data ? <>
          <StateChip state={p.data.state} />
          <Link to={`/activity/${t.proposal_id}`}>{p.data.state === 'pending_approval' ? 'Open the change waiting for a signature' : 'Open the change'}</Link>
        </> : <span>{t.status === 'done' ? 'Nothing to change' : ['escalated', 'cancelled'].includes(t.status) ? 'The designer could not do this; it is under Needs you' : 'Working on it'}</span>}
        <Time iso={t.created_at} />
      </div>
    </li>
  );
}
